import React, { useState, useMemo, useEffect } from 'react';
import {
  Sparkles, Shield, Database, Play, Check,
  Building2, Layers, BarChart3,
  FileSpreadsheet, ArrowRight, Lock, Clock, Users,
  Info, Zap, Eye, RefreshCw, Smartphone, Radio,
  GraduationCap, UserCheck, KeyRound, Bookmark,
  ArrowLeft, Save
} from 'lucide-react';
import { toast } from 'sonner';
import type { EscolaItem } from './ReportWorkbench';

export type DominioFocoKey =
  | 'foco_rede'
  | 'foco_rfid'
  | 'foco_aparelhos'
  | 'foco_professores'
  | 'foco_alunos'
  | 'foco_responsaveis';

export interface SavedCustomReport {
  id: string;
  title: string;
  foco: DominioFocoKey;
  dimensoes: string[];
  metricas: string[];
  escolaId: string;
  periodo: string;
  formato: string;
  criadoEm: string;
  autorNome?: string;
  promptOriginal?: string;
}

interface CustomReportBuilderProps {
  escolas: EscolaItem[];
  municipioId?: string;
  autorNome?: string;
  initialReport?: SavedCustomReport | null;
  onBackToGallery?: () => void;
  onSaveCustomReport?: (report: SavedCustomReport) => void;
  onExecuteCustomReport: (reportSpec: {
    id: string;
    title: string;
    escolaId: string;
    periodo: string;
    foco: string;
    dimensoes: string[];
    metricas: string[];
    formato: string;
  }) => void;
}

interface DominioFoco {
  id: DominioFocoKey;
  label: string;
  icon: any;
  desc: string;
  color: string;
  dimensoes: { id: string; label: string; icon: any; desc: string }[];
  metricas: { id: string; label: string; desc: string }[];
}

export const DOMINIOS_DISPONIVEIS: DominioFoco[] = [
  {
    id: 'foco_rede',
    label: 'Rede & Desempenho',
    icon: Building2,
    desc: 'Visão agregada e comparativa de frequência das escolas',
    color: '#2563eb',
    dimensoes: [
      { id: 'escola', label: 'Unidade Escolar', icon: Building2, desc: 'Agrupar por escola' },
      { id: 'turno', label: 'Turno Operacional', icon: Clock, desc: 'Manhã, Tarde, Noite, Integral' },
      { id: 'serie', label: 'Série / Ano', icon: Layers, desc: '1º ao 9º ano, Educação Infantil' },
      { id: 'tipo_incidente', label: 'Tipo de Ocorrência', icon: Shield, desc: 'Classificação de incidentes' },
    ],
    metricas: [
      { id: 'total_alunos', label: 'Total de Alunos', desc: 'Matriculados ativos' },
      { id: 'taxa_presenca', label: 'Taxa de Presença %', desc: 'Percentual de comparecimento' },
      { id: 'total_ausencias', label: 'Total de Faltas', desc: 'Registros de ausência' },
      { id: 'total_turmas', label: 'Total de Turmas', desc: 'Turmas ativas' },
      { id: 'total_incidentes', label: 'Total de Incidentes', desc: 'Ocorrências registradas' },
    ],
  },
  {
    id: 'foco_rfid',
    label: 'Acessos & RFID',
    icon: Radio,
    desc: 'Catracas, cancelas, cartões RFID e fluxo de portaria',
    color: '#0891b2',
    dimensoes: [
      { id: 'escola', label: 'Unidade Escolar', icon: Building2, desc: 'Escola de registro' },
      { id: 'ponto_acesso', label: 'Portaria / Ponto', icon: KeyRound, desc: 'Portão Principal, Refeitório, Quadra' },
      { id: 'sentido_passagem', label: 'Sentido (Entrada/Saída)', icon: Clock, desc: 'Direção do fluxo físico' },
      { id: 'tipo_usuario', label: 'Perfil de Usuário', icon: Users, desc: 'Aluno, Professor, Funcionário, Visitante' },
      { id: 'status_tag', label: 'Status da Tag RFID', icon: Radio, desc: 'Ativa, Bloqueada, Extraviada' },
    ],
    metricas: [
      { id: 'total_passagens', label: 'Total de Passagens', desc: 'Leituras registradas na catraca' },
      { id: 'passagens_autorizadas', label: 'Passagens Autorizadas', desc: 'Acessos liberados' },
      { id: 'acessos_bloqueados', label: 'Tentativas Bloqueadas', desc: 'Fora de turno ou tag inválida' },
      { id: 'tags_ativas', label: 'Cartões RFID Ativos', desc: 'Total de crachás vinculados' },
      { id: 'leitores_online', label: 'Catracas Online %', desc: 'Disponibilidade dos equipamentos' },
    ],
  },
  {
    id: 'foco_aparelhos',
    label: 'Aparelhos & Mobile',
    icon: Smartphone,
    desc: 'Smartphones dos pais, tokens Push, Android/iOS e segurança',
    color: '#7c3aed',
    dimensoes: [
      { id: 'escola', label: 'Unidade Escolar', icon: Building2, desc: 'Escola vinculada ao aluno' },
      { id: 'sistema_operacional', label: 'Sistema Operacional', icon: Smartphone, desc: 'Android vs iOS' },
      { id: 'fabricante_aparelho', label: 'Fabricante do Celular', icon: Smartphone, desc: 'Samsung, Motorola, Apple, Xiaomi' },
      { id: 'status_conformidade', label: 'Status de Segurança', icon: Shield, desc: 'Aprovado, Pendente, Biometria OK' },
    ],
    metricas: [
      { id: 'total_aparelhos_ativos', label: 'Aparelhos Conectados', desc: 'Smartphones autorizados' },
      { id: 'taxa_entrega_push', label: 'Entregabilidade Push %', desc: 'Alertas recebidos com sucesso' },
      { id: 'dispositivos_aprovados', label: 'Aparelhos com Biometria', desc: 'Conformidade de segurança' },
      { id: 'falhas_notificacao', label: 'Falhas de Envio', desc: 'Aparelhos sem token válido' },
    ],
  },
  {
    id: 'foco_professores',
    label: 'Quadro de Professores',
    icon: GraduationCap,
    desc: 'Listagem e alocação de docentes por matéria e escola',
    color: '#059669',
    dimensoes: [
      { id: 'escola', label: 'Unidade Escolar', icon: Building2, desc: 'Escola de lotação' },
      { id: 'disciplina', label: 'Disciplina / Matéria', icon: Layers, desc: 'Matemática, Português, Ciências...' },
      { id: 'regime_trabalho', label: 'Carga Horária', icon: Clock, desc: '20h, 40h, Dedicação Exclusiva' },
    ],
    metricas: [
      { id: 'total_professores', label: 'Total de Docentes', desc: 'Professores cadastrados' },
      { id: 'total_turmas_atrib', label: 'Turmas Atribuídas', desc: 'Média de turmas por professor' },
      { id: 'chamadas_realizadas', label: 'Chamadas Feitas %', desc: 'Assiduidade no diário eletrônico' },
      { id: 'substituicoes_mes', label: 'Substituições de Aulas', desc: 'Afastamentos supridos' },
    ],
  },
  {
    id: 'foco_alunos',
    label: 'Alunos & Matrículas',
    icon: Users,
    desc: 'Cadastro geral, séries, turmas e infrequência individual',
    color: '#d97706',
    dimensoes: [
      { id: 'escola', label: 'Unidade Escolar', icon: Building2, desc: 'Escola matriculada' },
      { id: 'serie', label: 'Série / Ano', icon: Layers, desc: '1º ao 9º ano, EJA' },
      { id: 'turma', label: 'Turma', icon: Users, desc: 'Identificador da turma' },
      { id: 'turno', label: 'Turno', icon: Clock, desc: 'Manhã, Tarde, Noite' },
    ],
    metricas: [
      { id: 'total_alunos', label: 'Total de Matriculados', desc: 'Alunos ativos na rede' },
      { id: 'taxa_presenca', label: 'Taxa de Comparecimento %', desc: 'Presença média no período' },
      { id: 'total_ausencias', label: 'Faltas Acumuladas', desc: 'Ausências registradas' },
      { id: 'alunos_em_risco', label: 'Alunos em Risco de Evasão', desc: 'Infrequência acima de 15%' },
    ],
  },
  {
    id: 'foco_responsaveis',
    label: 'Famílias & Responsáveis',
    icon: UserCheck,
    desc: 'Vínculos familiares, WhatsApp, telefonia e adesão ao app',
    color: '#db2777',
    dimensoes: [
      { id: 'escola', label: 'Unidade Escolar', icon: Building2, desc: 'Escola do dependente' },
      { id: 'grau_parentesco', label: 'Parentesco', icon: Users, desc: 'Mãe, Pai, Avós, Tutor' },
      { id: 'status_app', label: 'Situação do Aplicativo', icon: Smartphone, desc: 'Conta Ativa, Pendente, Sem Conta' },
    ],
    metricas: [
      { id: 'total_responsaveis', label: 'Total de Responsáveis', desc: 'Pais cadastrados' },
      { id: 'taxa_adesao_app', label: 'Adesão ao App %', desc: 'Percentual com aplicativo ativo' },
      { id: 'contatos_whatsapp', label: 'Contatos com WhatsApp', desc: 'Números válidos na base' },
      { id: 'visualizacao_recados', label: 'Leitura de Comunicados %', desc: 'Engajamento com avisos' },
    ],
  },
];

const TEMPLATE_PROMPTS = [
  {
    label: 'Fluxo de Catracas e RFID por Portaria',
    prompt: 'Gerar relatório de passagens em catracas, cartões RFID autorizados e acessos bloqueados por escola',
    foco: 'foco_rfid' as DominioFocoKey,
    title: 'Fluxo de Catracas e Controle de Acesso RFID — Rede Municipal',
    dimensoes: ['escola', 'ponto_acesso', 'sentido_passagem'],
    metricas: ['total_passagens', 'passagens_autorizadas', 'acessos_bloqueados', 'tags_ativas'],
    periodo: 'mes_atual',
    formato: 'ranking',
  },
  {
    label: 'Aparelhos Móveis dos Pais e Push',
    prompt: 'Mapear smartphones dos pais, sistema operacional Android/iOS e entregabilidade de notificações push',
    foco: 'foco_aparelhos' as DominioFocoKey,
    title: 'Parque de Dispositivos Móveis e Entregabilidade Push — Rede Municipal',
    dimensoes: ['escola', 'sistema_operacional', 'fabricante_aparelho'],
    metricas: ['total_aparelhos_ativos', 'taxa_entrega_push', 'dispositivos_aprovados'],
    periodo: 'mes_atual',
    formato: 'tabela',
  },
  {
    label: 'Quadro Docente por Disciplina',
    prompt: 'Listar todos os professores da rede distribuídos por disciplina e carga de turmas atribuídas',
    foco: 'foco_professores' as DominioFocoKey,
    title: 'Quadro Geral de Professores por Disciplina — Rede Municipal',
    dimensoes: ['escola', 'disciplina'],
    metricas: ['total_professores', 'total_turmas_atrib', 'chamadas_realizadas'],
    periodo: 'mes_atual',
    formato: 'tabela',
  },
  {
    label: 'Alunos em Risco de Evasão por Série',
    prompt: 'Relatório de alunos matriculados, faltas acumuladas e taxa de presença por série e turno',
    foco: 'foco_alunos' as DominioFocoKey,
    title: 'Diagnóstico de Presença e Risco de Evasão por Série — Rede Municipal',
    dimensoes: ['escola', 'serie', 'turno'],
    metricas: ['total_alunos', 'taxa_presenca', 'total_ausencias', 'alunos_em_risco'],
    periodo: 'mes_atual',
    formato: 'ranking',
  },
  {
    label: 'Cobertura Familiar e Contatos WhatsApp',
    prompt: 'Cadastro de responsáveis, adesão ao app e telefones celulares para envio de comunicados',
    foco: 'foco_responsaveis' as DominioFocoKey,
    title: 'Mapeamento de Famílias e Cobertura WhatsApp — Rede Municipal',
    dimensoes: ['escola', 'grau_parentesco', 'status_app'],
    metricas: ['total_responsaveis', 'taxa_adesao_app', 'contatos_whatsapp', 'visualizacao_recados'],
    periodo: 'mes_atual',
    formato: 'tabela',
  },
  {
    label: 'Frequência Geral por Turno e Escola',
    prompt: 'Comparativo geral de presença e ausências por turno em todas as escolas',
    foco: 'foco_rede' as DominioFocoKey,
    title: 'Comparativo Geral de Frequência por Turno — Rede Municipal',
    dimensoes: ['escola', 'turno'],
    metricas: ['total_alunos', 'taxa_presenca', 'total_ausencias'],
    periodo: 'mes_atual',
    formato: 'ranking',
  },
];

export function CustomReportBuilder({
  escolas,
  municipioId,
  autorNome,
  initialReport,
  onBackToGallery,
  onSaveCustomReport,
  onExecuteCustomReport,
}: CustomReportBuilderProps) {
  const [selectedFoco, setSelectedFoco] = useState<DominioFocoKey>(
    initialReport?.foco || 'foco_rede'
  );
  const [reportTitle, setReportTitle] = useState(
    initialReport?.title || 'Comparativo Geral de Frequência — Rede Municipal'
  );
  const [selectedEscolaId, setSelectedEscolaId] = useState(
    initialReport?.escolaId || 'todas'
  );
  const [selectedPeriodo, setSelectedPeriodo] = useState(
    initialReport?.periodo || 'mes_atual'
  );
  const [selectedDimensoes, setSelectedDimensoes] = useState<string[]>(
    initialReport?.dimensoes || ['escola', 'turno']
  );
  const [selectedMetricas, setSelectedMetricas] = useState<string[]>(
    initialReport?.metricas || ['total_alunos', 'taxa_presenca', 'total_ausencias']
  );
  const [selectedFormato, setSelectedFormato] = useState(
    initialReport?.formato || 'ranking'
  );
  const [aiPrompt, setAiPrompt] = useState(initialReport?.promptOriginal || '');
  const [isInterpretingAi, setIsInterpretingAi] = useState(false);

  useEffect(() => {
    if (initialReport) {
      setSelectedFoco(initialReport.foco);
      setReportTitle(initialReport.title);
      setSelectedEscolaId(initialReport.escolaId);
      setSelectedPeriodo(initialReport.periodo);
      setSelectedDimensoes(initialReport.dimensoes);
      setSelectedMetricas(initialReport.metricas);
      setSelectedFormato(initialReport.formato || 'ranking');
      if (initialReport.promptOriginal) setAiPrompt(initialReport.promptOriginal);
    }
  }, [initialReport]);

  // Domínio ativo
  const currentDomain = useMemo(
    () => DOMINIOS_DISPONIVEIS.find((d) => d.id === selectedFoco) || DOMINIOS_DISPONIVEIS[0],
    [selectedFoco]
  );

  // Helper para gerar título inteligente
  const generateIntelligentTitle = (
    foco: DominioFocoKey,
    dimensoes: string[],
    metricas: string[],
    escolaId: string
  ) => {
    const nomeEscola =
      escolaId !== 'todas'
        ? escolas.find((e) => e.id === escolaId)?.nome || 'Unidade'
        : 'Rede Municipal';

    switch (foco) {
      case 'foco_rfid':
        if (dimensoes.includes('ponto_acesso')) return `Fluxo de Portarias e Catracas RFID — ${nomeEscola}`;
        if (dimensoes.includes('status_tag')) return `Auditoria de Cartões RFID e Bloqueios — ${nomeEscola}`;
        return `Controle de Acesso Físico e RFID — ${nomeEscola}`;

      case 'foco_aparelhos':
        if (dimensoes.includes('sistema_operacional')) return `Parque de Dispositivos Android/iOS dos Pais — ${nomeEscola}`;
        return `Aparelhos Autorizados e Telemetria Push — ${nomeEscola}`;

      case 'foco_professores':
        if (dimensoes.includes('disciplina')) return `Quadro Docente por Disciplina — ${nomeEscola}`;
        return `Lotação e Assiduidade de Professores — ${nomeEscola}`;

      case 'foco_alunos':
        if (dimensoes.includes('serie')) return `Presença e Evasão de Alunos por Série — ${nomeEscola}`;
        return `Listagem e Frequência de Estudantes — ${nomeEscola}`;

      case 'foco_responsaveis':
        if (dimensoes.includes('status_app')) return `Adesão das Famílias ao Aplicativo — ${nomeEscola}`;
        return `Cadastro e Canais de Contato com Pais — ${nomeEscola}`;

      case 'foco_rede':
      default:
        if (dimensoes.includes('turno')) return `Comparativo de Frequência por Turno — ${nomeEscola}`;
        return `Relatório de Gestão e Desempenho — ${nomeEscola}`;
    }
  };

  const handleSelectFoco = (focoKey: DominioFocoKey) => {
    setSelectedFoco(focoKey);
    const domain = DOMINIOS_DISPONIVEIS.find((d) => d.id === focoKey) || DOMINIOS_DISPONIVEIS[0];
    const initialDims = [domain.dimensoes[0].id, domain.dimensoes[1]?.id].filter(Boolean);
    const initialMets = [domain.metricas[0].id, domain.metricas[1]?.id, domain.metricas[2]?.id].filter(Boolean);

    setSelectedDimensoes(initialDims);
    setSelectedMetricas(initialMets);
    setReportTitle(generateIntelligentTitle(focoKey, initialDims, initialMets, selectedEscolaId));
  };

  const toggleDimensao = (id: string) => {
    setSelectedDimensoes((prev) => {
      const next = prev.includes(id)
        ? prev.length > 1
          ? prev.filter((d) => d !== id)
          : prev
        : [...prev, id];
      setReportTitle(generateIntelligentTitle(selectedFoco, next, selectedMetricas, selectedEscolaId));
      return next;
    });
  };

  const toggleMetrica = (id: string) => {
    setSelectedMetricas((prev) => {
      const next = prev.includes(id)
        ? prev.length > 1
          ? prev.filter((m) => m !== id)
          : prev
        : [...prev, id];
      setReportTitle(generateIntelligentTitle(selectedFoco, selectedDimensoes, next, selectedEscolaId));
      return next;
    });
  };

  const handleEscolaChange = (id: string) => {
    setSelectedEscolaId(id);
    setReportTitle(generateIntelligentTitle(selectedFoco, selectedDimensoes, selectedMetricas, id));
  };

  const handleApplyTemplate = (tpl: (typeof TEMPLATE_PROMPTS)[0]) => {
    setSelectedFoco(tpl.foco);
    setAiPrompt(tpl.prompt);
    setReportTitle(tpl.title);
    setSelectedDimensoes(tpl.dimensoes);
    setSelectedMetricas(tpl.metricas);
    setSelectedPeriodo(tpl.periodo);
    setSelectedFormato(tpl.formato || 'tabela');
    toast.success(`Modelo "${tpl.label}" carregado.`);
  };

  const handleAiInterpret = () => {
    if (!aiPrompt.trim()) {
      toast.error('Digite uma instrução para a IA.');
      return;
    }
    setIsInterpretingAi(true);

    setTimeout(() => {
      setIsInterpretingAi(false);
      const lower = aiPrompt.toLowerCase();

      let targetFoco: DominioFocoKey = 'foco_rede';

      if (lower.includes('rfid') || lower.includes('catraca') || lower.includes('portaria') || lower.includes('acesso') || lower.includes('cancela')) {
        targetFoco = 'foco_rfid';
      } else if (lower.includes('aparelho') || lower.includes('celular') || lower.includes('smartphone') || lower.includes('push') || lower.includes('android') || lower.includes('ios') || lower.includes('dispositivo')) {
        targetFoco = 'foco_aparelhos';
      } else if (lower.includes('professor') || lower.includes('docente') || lower.includes('materia') || lower.includes('disciplina')) {
        targetFoco = 'foco_professores';
      } else if (lower.includes('aluno') || lower.includes('matricula') || lower.includes('estudante')) {
        targetFoco = 'foco_alunos';
      } else if (lower.includes('responsavel') || lower.includes('pais') || lower.includes('mae') || lower.includes('pai') || lower.includes('familia') || lower.includes('whatsapp')) {
        targetFoco = 'foco_responsaveis';
      }

      setSelectedFoco(targetFoco);
      const domain = DOMINIOS_DISPONIVEIS.find((d) => d.id === targetFoco) || DOMINIOS_DISPONIVEIS[0];

      let newDims = [domain.dimensoes[0].id];
      domain.dimensoes.forEach((d) => {
        if (lower.includes(d.id) || lower.includes(d.label.toLowerCase())) {
          newDims.push(d.id);
        }
      });
      if (newDims.length === 1 && domain.dimensoes[1]) {
        newDims.push(domain.dimensoes[1].id);
      }

      let newMets = [domain.metricas[0].id];
      domain.metricas.forEach((m) => {
        if (lower.includes(m.id) || lower.includes(m.label.toLowerCase())) {
          newMets.push(m.id);
        }
      });
      if (newMets.length === 1 && domain.metricas[1]) {
        newMets.push(domain.metricas[1].id);
        if (domain.metricas[2]) newMets.push(domain.metricas[2].id);
      }

      newDims = Array.from(new Set(newDims));
      newMets = Array.from(new Set(newMets));

      const newFormat = lower.includes('ranking') || lower.includes('comparativo') ? 'ranking' : 'tabela';

      setSelectedDimensoes(newDims);
      setSelectedMetricas(newMets);
      setSelectedFormato(newFormat);

      const titleGerado = generateIntelligentTitle(targetFoco, newDims, newMets, selectedEscolaId);
      setReportTitle(titleGerado);
      toast.success(`IA identificou o foco "${domain.label}" e estruturou o relatório!`);
    }, 500);
  };

  // Explicação narrativa da IA
  const aiExplanation = useMemo(() => {
    const nomesDims = selectedDimensoes
      .map((d) => currentDomain.dimensoes.find((x) => x.id === d)?.label || d)
      .join(', ');

    const nomesMets = selectedMetricas
      .map((m) => currentDomain.metricas.find((x) => x.id === m)?.label || m)
      .join(', ');

    const labelPeriodoMap: Record<string, string> = {
      hoje: 'o dia atual',
      ontem: 'o dia de ontem',
      '7dias': 'os últimos 7 dias',
      '30dias': 'os últimos 30 dias',
      mes_atual: 'o mês atual',
      mes_anterior: 'o mês anterior',
      ano_letivo: 'o ano letivo corrente',
    };

    const nomeEscola =
      selectedEscolaId !== 'todas'
        ? escolas.find((e) => e.id === selectedEscolaId)?.nome || 'unidade selecionada'
        : 'todas as unidades da rede municipal';

    return `Este relatório processará dados de ${nomeEscola} no domínio [${currentDomain.label}] agrupando por (${nomesDims}) durante ${
      labelPeriodoMap[selectedPeriodo] || 'o período selecionado'
    }. Métricas consolidadas: ${nomesMets}. Formato: ${
      selectedFormato === 'ranking' ? 'Ranking comparativo com ordenação' : 'Matriz analítica tabular'
    }.`;
  }, [selectedDimensoes, selectedMetricas, selectedPeriodo, selectedEscolaId, selectedFormato, currentDomain, escolas]);

  // Colunas da Prévia Dinâmica
  const previewColumns = useMemo(() => {
    const dimCols = selectedDimensoes.map(
      (d) => currentDomain.dimensoes.find((x) => x.id === d)?.label || d
    );
    const metCols = selectedMetricas.map(
      (m) => currentDomain.metricas.find((x) => x.id === m)?.label || m
    );
    return [...dimCols, ...metCols];
  }, [selectedDimensoes, selectedMetricas, currentDomain]);

  // Linhas Mockadas da Prévia Dinâmica por Domínio
  const previewRows = useMemo(() => {
    const domainMockData: Record<DominioFocoKey, any[]> = {
      foco_rfid: [
        {
          escola: 'Escola Santos Dumont',
          ponto_acesso: 'Portaria Principal (Catraca 01)',
          sentido_passagem: 'ENTRADA',
          tipo_usuario: 'Aluno',
          status_tag: 'ATIVA',
          total_passagens: '1.420',
          passagens_autorizadas: '1.408 (99.1%)',
          acessos_bloqueados: '12',
          tags_ativas: '520',
          leitores_online: '100%',
        },
        {
          escola: 'EMEF Tiradentes',
          ponto_acesso: 'Portão dos Fundos (Catraca 02)',
          sentido_passagem: 'ENTRADA',
          tipo_usuario: 'Aluno',
          status_tag: 'ATIVA',
          total_passagens: '980',
          passagens_autorizadas: '954 (97.3%)',
          acessos_bloqueados: '26',
          tags_ativas: '380',
          leitores_online: '100%',
        },
        {
          escola: 'Colégio Ayrton Senna',
          ponto_acesso: 'Acesso Quadra Esportiva',
          sentido_passagem: 'SAÍDA',
          tipo_usuario: 'Professor / Funcionário',
          status_tag: 'ATIVA',
          total_passagens: '340',
          passagens_autorizadas: '340 (100%)',
          acessos_bloqueados: '0',
          tags_ativas: '48',
          leitores_online: '100%',
        },
      ],
      foco_aparelhos: [
        {
          escola: 'Escola Santos Dumont',
          sistema_operacional: 'Android 14',
          fabricante_aparelho: 'Samsung',
          status_conformidade: 'Biometria Aprovada',
          total_aparelhos_ativos: '390',
          taxa_entrega_push: '98.2%',
          dispositivos_aprovados: '375',
          falhas_notificacao: '7',
        },
        {
          escola: 'EMEF Tiradentes',
          sistema_operacional: 'iOS 17.5',
          fabricante_aparelho: 'Apple',
          status_conformidade: 'Biometria Aprovada',
          total_aparelhos_ativos: '145',
          taxa_entrega_push: '99.1%',
          dispositivos_aprovados: '142',
          falhas_notificacao: '1',
        },
        {
          escola: 'Colégio Ayrton Senna',
          sistema_operacional: 'Android 13',
          fabricante_aparelho: 'Motorola',
          status_conformidade: 'Pendente Token',
          total_aparelhos_ativos: '210',
          taxa_entrega_push: '91.4%',
          dispositivos_aprovados: '190',
          falhas_notificacao: '18',
        },
      ],
      foco_professores: [
        {
          escola: 'Escola Santos Dumont',
          disciplina: 'Matemática',
          regime_trabalho: '40h Semanais',
          total_professores: '6',
          total_turmas_atrib: '12 turmas',
          chamadas_realizadas: '98.5%',
          substituicoes_mes: '1',
        },
        {
          escola: 'EMEF Tiradentes',
          disciplina: 'Língua Portuguesa',
          regime_trabalho: '20h Semanais',
          total_professores: '4',
          total_turmas_atrib: '8 turmas',
          chamadas_realizadas: '94.0%',
          substituicoes_mes: '3',
        },
        {
          escola: 'Colégio Ayrton Senna',
          disciplina: 'Ciências da Natureza',
          regime_trabalho: '40h Semanais',
          total_professores: '5',
          total_turmas_atrib: '10 turmas',
          chamadas_realizadas: '97.2%',
          substituicoes_mes: '0',
        },
      ],
      foco_alunos: [
        {
          escola: 'Escola Santos Dumont',
          serie: '5º Ano',
          turma: 'Turma A',
          turno: 'Manhã',
          total_alunos: '32',
          taxa_presenca: '96.8%',
          total_ausencias: '11',
          alunos_em_risco: '1',
        },
        {
          escola: 'EMEF Tiradentes',
          serie: '8º Ano',
          turma: 'Turma B',
          turno: 'Tarde',
          total_alunos: '30',
          taxa_presenca: '88.4%',
          total_ausencias: '38',
          alunos_em_risco: '4',
        },
        {
          escola: 'Colégio Ayrton Senna',
          serie: '3º Ano',
          turma: 'Turma Única',
          turno: 'Integral',
          total_alunos: '28',
          taxa_presenca: '95.1%',
          total_ausencias: '15',
          alunos_em_risco: '0',
        },
      ],
      foco_responsaveis: [
        {
          escola: 'Escola Santos Dumont',
          grau_parentesco: 'Mãe',
          status_app: 'Conta Ativa',
          total_responsaveis: '310',
          taxa_adesao_app: '94.5%',
          contatos_whatsapp: '298',
          visualizacao_recados: '91.2%',
        },
        {
          escola: 'EMEF Tiradentes',
          grau_parentesco: 'Pai',
          status_app: 'Pendente',
          total_responsaveis: '185',
          taxa_adesao_app: '72.0%',
          contatos_whatsapp: '160',
          visualizacao_recados: '68.5%',
        },
        {
          escola: 'Colégio Ayrton Senna',
          grau_parentesco: 'Avó / Tutor',
          status_app: 'Conta Ativa',
          total_responsaveis: '65',
          taxa_adesao_app: '88.0%',
          contatos_whatsapp: '60',
          visualizacao_recados: '85.0%',
        },
      ],
      foco_rede: [
        {
          escola: 'Escola Santos Dumont',
          turno: 'Manhã',
          serie: 'Fundamental I',
          tipo_incidente: 'Atraso Coletivo',
          total_alunos: '420',
          taxa_presenca: '96.4%',
          total_ausencias: '15',
          total_turmas: '14',
          total_incidentes: '2',
        },
        {
          escola: 'EMEF Tiradentes',
          turno: 'Tarde',
          serie: 'Fundamental II',
          tipo_incidente: 'Ocorrência Disciplinar',
          total_alunos: '380',
          taxa_presenca: '89.2%',
          total_ausencias: '41',
          total_turmas: '12',
          total_incidentes: '7',
        },
        {
          escola: 'Colégio Ayrton Senna',
          turno: 'Integral',
          serie: 'Educação Infantil',
          tipo_incidente: 'Nenhum',
          total_alunos: '510',
          taxa_presenca: '94.8%',
          total_ausencias: '26',
          total_turmas: '18',
          total_incidentes: '0',
        },
      ],
    };

    const samples = domainMockData[selectedFoco] || domainMockData.foco_rede;

    return samples.map((sample: any) => {
      const row: string[] = [];
      selectedDimensoes.forEach((d) => row.push(sample[d] || '—'));
      selectedMetricas.forEach((m) => row.push(sample[m] || '—'));
      return row;
    });
  }, [selectedFoco, selectedDimensoes, selectedMetricas]);

  const handleExecute = () => {
    if (selectedDimensoes.length === 0) {
      toast.error('Selecione pelo menos uma dimensão.');
      return;
    }
    if (selectedMetricas.length === 0) {
      toast.error('Selecione pelo menos uma métrica.');
      return;
    }

    onExecuteCustomReport({
      id: 'custom-' + Date.now(),
      title: reportTitle || 'Relatório Personalizado de Gestão',
      escolaId: selectedEscolaId,
      periodo: selectedPeriodo,
      foco: selectedFoco,
      dimensoes: selectedDimensoes,
      metricas: selectedMetricas,
      formato: selectedFormato,
    });
  };

  const handleSave = () => {
    if (selectedDimensoes.length === 0 || selectedMetricas.length === 0) {
      toast.error('Configure dimensões e métricas antes de salvar.');
      return;
    }

    const newReport: SavedCustomReport = {
      id: initialReport?.id || 'custom-' + Date.now(),
      title: reportTitle || 'Relatório Personalizado de Gestão',
      foco: selectedFoco,
      dimensoes: selectedDimensoes,
      metricas: selectedMetricas,
      escolaId: selectedEscolaId,
      periodo: selectedPeriodo,
      formato: selectedFormato,
      criadoEm: initialReport?.criadoEm || new Date().toISOString(),
      autorNome: initialReport?.autorNome || autorNome || 'Gestor Municipal',
      promptOriginal: aiPrompt || undefined,
    };

    if (onSaveCustomReport) {
      onSaveCustomReport(newReport);
    }
  };

  return (
    <div className="rpt-custom-builder">
      {/* ─── BARRA DE NAVEGAÇÃO DE VOLTA (SE HOUVER GALERIA) ─── */}
      {onBackToGallery && (
        <div className="rpt-cb-top-nav">
          <button
            type="button"
            className="rpt-cb-back-gallery-btn"
            onClick={onBackToGallery}
          >
            <ArrowLeft size={13} />
            <span>Voltar para a Galeria de Salvos</span>
          </button>
          <span className="rpt-cb-editing-badge">
            {initialReport ? 'Modo de Edição de Modelo' : 'Criando Novo Relatório Personalizado'}
          </span>
        </div>
      )}

      {/* ─── HEADER COM GRADIENTE E BADGES DE SEGURANÇA ─── */}
      <div className="rpt-cb-hero">
        <div className="rpt-cb-hero-left">
          <div className="rpt-cb-hero-badge">
            <Sparkles size={13} />
            <span>Inteligência & Camada Semântica</span>
          </div>
          <h2 className="rpt-cb-hero-title">Construtor de Relatórios Personalizados & Telemetria</h2>
          <p className="rpt-cb-hero-desc">
            Crie análises da rede cruzando catracas RFID, smartphones dos pais, quadro docente,
            estudantes e indicadores de gestão com isolamento multi-tenant obrigatório.
          </p>
          <div className="rpt-cb-security-badges">
            <span className="rpt-cb-sec-pill">
              <Shield size={11} /> Anti-IDOR: Escopo Municipal Obrigatório
            </span>
            <span className="rpt-cb-sec-pill">
              <Lock size={11} /> Sem acesso direto a SQL bruto
            </span>
            <span className="rpt-cb-sec-pill">
              <Database size={11} /> PostgreSQL Parametrizado
            </span>
          </div>
        </div>
      </div>

      {/* ─── SELETOR DE DOMÍNIO / FOCO DA GESTÃO ─── */}
      <div className="rpt-cb-foco-selector-bar">
        <span className="rpt-cb-foco-label">Foco do Relatório:</span>
        <div className="rpt-cb-foco-track">
          {DOMINIOS_DISPONIVEIS.map((dom) => {
            const Icon = dom.icon;
            const isSelected = selectedFoco === dom.id;
            return (
              <button
                key={dom.id}
                type="button"
                className={`rpt-cb-foco-btn ${isSelected ? 'rpt-cb-foco-btn-active' : ''}`}
                onClick={() => handleSelectFoco(dom.id)}
                style={{
                  borderLeftColor: isSelected ? dom.color : 'transparent',
                }}
              >
                <div
                  className="rpt-cb-foco-icon"
                  style={{
                    background: isSelected ? `${dom.color}20` : '#f1f5f9',
                    color: isSelected ? dom.color : '#64748b',
                  }}
                >
                  <Icon size={14} />
                </div>
                <div className="rpt-cb-foco-text">
                  <span className="rpt-cb-foco-name">{dom.label}</span>
                  <span className="rpt-cb-foco-desc">{dom.desc}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── SEÇÃO 1: IA COPILOT / PROMPT STUDIO ─── */}
      <div className="rpt-cb-section rpt-cb-ai-studio">
        <div className="rpt-cb-section-header">
          <div className="rpt-cb-sec-title">
            <Sparkles size={16} className="rpt-cb-ai-glow" />
            <span>Assistente IA (Descreva em Linguagem Natural)</span>
          </div>
          <span className="rpt-cb-sec-hint">
            A IA detecta automaticamente o domínio (RFID, Aparelhos, Professores, Alunos, Famílias) e gera a prévia
          </span>
        </div>

        <div className="rpt-cb-prompt-box">
          <textarea
            className="rpt-cb-prompt-input"
            rows={2}
            placeholder="Ex: Mapear fluxo de catracas RFID e acessos bloqueados por portaria em todas as escolas no mês atual..."
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
          />
          <button
            type="button"
            className="rpt-cb-prompt-btn"
            onClick={handleAiInterpret}
            disabled={isInterpretingAi || !aiPrompt.trim()}
          >
            {isInterpretingAi ? (
              <>
                <RefreshCw size={13} className="rpt-spin-fast" />
                <span>Interpretando...</span>
              </>
            ) : (
              <>
                <Sparkles size={13} />
                <span>Interpretar com IA</span>
              </>
            )}
          </button>
        </div>

        {/* Modelos e Prompts Sugeridos */}
        <div className="rpt-cb-templates-row">
          <span className="rpt-cb-tpl-label">Sugestões Rápidas:</span>
          {TEMPLATE_PROMPTS.map((tpl, i) => (
            <button
              key={i}
              type="button"
              className="rpt-cb-tpl-chip"
              onClick={() => handleApplyTemplate(tpl)}
            >
              <Zap size={11} />
              <span>{tpl.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ─── SEÇÃO 2: CONSTRUTOR EM 3 COLUNAS (ESCOPO / MÉTRICAS / PRÉVIA DINÂMICA) ─── */}
      <div className="rpt-cb-grid-builder-3col">
        {/* COLUNA 1: ESCOPO & DIMENSÕES DE AGRUPAMENTO */}
        <div className="rpt-cb-card">
          <div className="rpt-cb-card-title">
            <Building2 size={15} />
            <span>1. Escopo & Dimensões (Linhas)</span>
          </div>

          <div className="rpt-cb-field">
            <label className="rpt-cb-label">Nome do Relatório:</label>
            <input
              type="text"
              className="rpt-cb-input-text"
              value={reportTitle}
              onChange={(e) => setReportTitle(e.target.value)}
              placeholder="Digite o título do relatório..."
            />
          </div>

          <div className="rpt-cb-fields-row">
            <div className="rpt-cb-field" style={{ flex: 1 }}>
              <label className="rpt-cb-label">Unidade Escolar:</label>
              <select
                className="rpt-cb-select"
                value={selectedEscolaId}
                onChange={(e) => handleEscolaChange(e.target.value)}
              >
                <option value="todas">🌐 Todas as Escolas da Rede</option>
                {escolas.map((esc) => (
                  <option key={esc.id} value={esc.id}>
                    🏫 {esc.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="rpt-cb-field" style={{ flex: 1 }}>
              <label className="rpt-cb-label">Período Analítico:</label>
              <select
                className="rpt-cb-select"
                value={selectedPeriodo}
                onChange={(e) => setSelectedPeriodo(e.target.value)}
              >
                <option value="hoje">📅 Hoje</option>
                <option value="ontem">📅 Ontem</option>
                <option value="7dias">📅 Últimos 7 dias</option>
                <option value="30dias">📅 Últimos 30 dias</option>
                <option value="mes_atual">📅 Mês Atual</option>
                <option value="mes_anterior">📅 Mês Anterior</option>
                <option value="ano_letivo">📅 Ano Letivo</option>
              </select>
            </div>
          </div>

          <div className="rpt-cb-field" style={{ marginTop: 6 }}>
            <label className="rpt-cb-label">Dimensões de [{currentDomain.label}]:</label>
            <div className="rpt-cb-chips-list">
              {currentDomain.dimensoes.map((dim) => {
                const active = selectedDimensoes.includes(dim.id);
                const Icon = dim.icon;
                return (
                  <button
                    key={dim.id}
                    type="button"
                    className={`rpt-cb-chip-item ${active ? 'rpt-cb-chip-active' : ''}`}
                    onClick={() => toggleDimensao(dim.id)}
                  >
                    <Icon size={13} />
                    <div className="rpt-cb-chip-text">
                      <span className="rpt-cb-chip-name">{dim.label}</span>
                      <span className="rpt-cb-chip-desc">{dim.desc}</span>
                    </div>
                    {active && <Check size={13} className="rpt-cb-check-icon" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* COLUNA 2: MÉTRICAS & INDICADORES */}
        <div className="rpt-cb-card">
          <div className="rpt-cb-card-title">
            <BarChart3 size={15} />
            <span>2. Métricas & Indicadores (Colunas)</span>
          </div>

          <div className="rpt-cb-chips-list">
            {currentDomain.metricas.map((met) => {
              const active = selectedMetricas.includes(met.id);
              return (
                <button
                  key={met.id}
                  type="button"
                  className={`rpt-cb-chip-item ${active ? 'rpt-cb-chip-active' : ''}`}
                  onClick={() => toggleMetrica(met.id)}
                >
                  <div className="rpt-cb-chip-text">
                    <span className="rpt-cb-chip-name">{met.label}</span>
                    <span className="rpt-cb-chip-desc">{met.desc}</span>
                  </div>
                  {active && <Check size={13} className="rpt-cb-check-icon" />}
                </button>
              );
            })}
          </div>

          <div className="rpt-cb-field" style={{ marginTop: 6 }}>
            <label className="rpt-cb-label">Apresentação:</label>
            <div className="rpt-cb-format-options">
              <button
                type="button"
                className={`rpt-cb-format-btn ${selectedFormato === 'ranking' ? 'rpt-cb-format-active' : ''}`}
                onClick={() => setSelectedFormato('ranking')}
              >
                <BarChart3 size={13} />
                <span>Ranking Comparativo</span>
              </button>
              <button
                type="button"
                className={`rpt-cb-format-btn ${selectedFormato === 'tabela' ? 'rpt-cb-format-active' : ''}`}
                onClick={() => setSelectedFormato('tabela')}
              >
                <FileSpreadsheet size={13} />
                <span>Tabela Analítica</span>
              </button>
            </div>
          </div>
        </div>

        {/* COLUNA 3: PRÉVIA DINÂMICA & EXPLICAÇÃO SEMÂNTICA DA IA */}
        <div className="rpt-cb-card rpt-cb-preview-card">
          <div className="rpt-cb-card-title">
            <Eye size={15} style={{ color: '#2563eb' }} />
            <span>3. Prévia Dinâmica [{currentDomain.label}]</span>
            <span className="rpt-cb-preview-tag">Live Mockup</span>
          </div>

          {/* Explicação da IA */}
          <div className="rpt-cb-ai-explanation">
            <div className="rpt-cb-ai-exp-head">
              <Sparkles size={13} style={{ color: '#4f46e5' }} />
              <span>Análise Semântica da IA:</span>
            </div>
            <p className="rpt-cb-ai-exp-text">{aiExplanation}</p>
          </div>

          {/* Tabela de Prévia Dinâmica em Tempo Real */}
          <div className="rpt-cb-table-preview-wrap">
            <div className="rpt-cb-table-preview-title">
              <FileSpreadsheet size={12} />
              <span>Estrutura de Saída ({previewColumns.length} colunas):</span>
            </div>
            <div className="rpt-cb-table-scroll">
              <table className="rpt-cb-table-preview">
                <thead>
                  <tr>
                    {previewColumns.map((col, idx) => (
                      <th key={idx}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, rIdx) => (
                    <tr key={rIdx}>
                      {row.map((cell, cIdx) => (
                        <td key={cIdx}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="rpt-cb-table-footer-hint">
              <Info size={11} />
              <span>Prévia estrutural — os dados reais serão consultados no banco ao executar.</span>
            </div>
          </div>

          {/* Ações: Salvar na Galeria + Gerar no Workbench */}
          <div className="rpt-cb-actions-split" style={{ marginTop: 'auto' }}>
            <button
              type="button"
              className="rpt-cb-save-gallery-btn"
              onClick={handleSave}
            >
              <Bookmark size={14} />
              <span>Salvar na Galeria</span>
            </button>

            <button
              type="button"
              className="rpt-cb-execute-btn"
              onClick={handleExecute}
            >
              <Play size={14} />
              <span>Gerar no Workbench</span>
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
